import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { ChevronDown, ChevronUp, Settings, Trash2, Plus } from "lucide-react"
import { InfoTip } from "@/components/custom/fire/info-tip"
import type { FireSettings, UpdateFireSettingsInput, ContributionStep, ScenarioOverrides } from "@/types/fire"
import { useCurrency } from "@/context/currency/currency-context"

interface FireSettingsPanelProps {
	settings: FireSettings
	onSave: (input: UpdateFireSettingsInput) => Promise<void>
	onSettingsChange?: (preview: Partial<UpdateFireSettingsInput>) => void
	saving?: boolean
	portfolioValue?: number // total current portfolio value in display currency (including cash)
	baseDivGrowth?: number | null // income-weighted avg of per-position 5Y CAGR (derived, read-only)
}

export function FireSettingsPanel({
	settings,
	onSave,
	onSettingsChange,
	saving,
	portfolioValue,
	baseDivGrowth,
}: FireSettingsPanelProps) {
	const { currency, convert } = useCurrency()
	const [expanded, setExpanded] = useState(false)
	const [advancedExpanded, setAdvancedExpanded] = useState(false)

	// Contribution schedule state
	const [useSchedule, setUseSchedule] = useState(
		settings.contribution_schedule != null && settings.contribution_schedule.length > 0,
	)
	const [scheduleSteps, setScheduleSteps] = useState<ContributionStep[]>(
		(settings.contribution_schedule ?? []).map((s) => ({
			...s,
			monthly_amount: convert(s.monthly_amount, settings.currency),
		})),
	)

	// Scenario override state — defaults match the plan defaults (3/2 conserv, 7/8 optim)
	const [scenarioRaw, setScenarioRaw] = useState({
		conserv_div_growth: String(settings.scenario_overrides?.conservative?.expected_dividend_growth ?? 3),
		conserv_price_appr: String(settings.scenario_overrides?.conservative?.price_appreciation ?? 2),
		optim_div_growth: String(settings.scenario_overrides?.optimistic?.expected_dividend_growth ?? 7),
		optim_price_appr: String(settings.scenario_overrides?.optimistic?.price_appreciation ?? 8),
	})
	const [scenarioOverrides, setScenarioOverrides] = useState<ScenarioOverrides>({
		conservative: {
			expected_dividend_growth: settings.scenario_overrides?.conservative?.expected_dividend_growth ?? 3,
			price_appreciation: settings.scenario_overrides?.conservative?.price_appreciation ?? 2,
		},
		optimistic: {
			expected_dividend_growth: settings.scenario_overrides?.optimistic?.expected_dividend_growth ?? 7,
			price_appreciation: settings.scenario_overrides?.optimistic?.price_appreciation ?? 8,
		},
	})

	// Raw string state for inputs — prevents zero-prefix bug where clearing a field
	// sets numeric state to 0, rendering "0" and causing "05" when user types next digit.
	const [rawInputs, setRawInputs] = useState({
		monthly_expense_target: String(Math.round(convert(settings.monthly_expense_target, settings.currency))),
		monthly_contribution: String(Math.round(convert(settings.monthly_contribution, settings.currency))),
		expected_dividend_growth: String(settings.expected_dividend_growth),
		price_appreciation: String(settings.price_appreciation),
		dividend_growth_cap: String(settings.dividend_growth_cap),
	})

	// Monetary fields are stored in the settings currency on the backend.
	// The form shows them in the user's display currency (converted on load, unconverted on save).
	const [form, setForm] = useState<UpdateFireSettingsInput>({
		monthly_expense_target: convert(settings.monthly_expense_target, settings.currency),
		monthly_contribution: convert(settings.monthly_contribution, settings.currency),
		expected_dividend_growth: settings.expected_dividend_growth,
		price_appreciation: settings.price_appreciation,
		dividend_growth_cap: settings.dividend_growth_cap,
		drip_enabled: settings.drip_enabled,
	})

	// Update form and notify parent for live projection preview
	const updateForm = (patch: Partial<UpdateFireSettingsInput>) => {
		setForm((f) => {
			const next = { ...f, ...patch }
			onSettingsChange?.({
				...next,
				monthly_expense_target: next.monthly_expense_target ?? 0,
				monthly_contribution: next.monthly_contribution ?? 0,
				currency: currency.code,
				contribution_schedule: useSchedule ? scheduleSteps.map((s) => ({ ...s })) : null,
				scenario_overrides: scenarioOverrides,
			})
			return next
		})
	}

	// Notify parent when schedule changes
	const updateSchedule = (steps: ContributionStep[]) => {
		setScheduleSteps(steps)
		onSettingsChange?.({
			...form,
			monthly_expense_target: form.monthly_expense_target ?? 0,
			monthly_contribution: form.monthly_contribution ?? 0,
			currency: currency.code,
			contribution_schedule: steps.map((s) => ({ ...s })),
		})
	}

	// Update a single scenario override field and notify parent
	const updateScenario = (patch: Partial<ScenarioOverrides>) => {
		setScenarioOverrides((prev) => {
			const next = { ...prev, ...patch }
			onSettingsChange?.({
				...form,
				monthly_expense_target: form.monthly_expense_target ?? 0,
				monthly_contribution: form.monthly_contribution ?? 0,
				currency: currency.code,
				contribution_schedule: useSchedule ? scheduleSteps.map((s) => ({ ...s })) : null,
				scenario_overrides: next,
			})
			return next
		})
	}

	// Toggle schedule on/off
	const handleToggleSchedule = (checked: boolean) => {
		setUseSchedule(checked)
		onSettingsChange?.({
			...form,
			monthly_expense_target: form.monthly_expense_target ?? 0,
			monthly_contribution: form.monthly_contribution ?? 0,
			currency: currency.code,
			contribution_schedule: checked ? scheduleSteps.map((s) => ({ ...s })) : null,
		})
	}

	// Re-convert monetary fields when the user switches display currency or fresh settings load.
	useEffect(() => {
		const expense = convert(settings.monthly_expense_target, settings.currency)
		const contribution = convert(settings.monthly_contribution, settings.currency)
		setForm((f) => ({ ...f, monthly_expense_target: expense, monthly_contribution: contribution }))
		setRawInputs((r) => ({
			...r,
			monthly_expense_target: String(Math.round(expense)),
			monthly_contribution: String(Math.round(contribution)),
		}))
	}, [currency.code, settings.monthly_expense_target, settings.monthly_contribution, settings.currency, convert])

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		await onSave({
			...form,
			monthly_expense_target: form.monthly_expense_target ?? 0,
			monthly_contribution: form.monthly_contribution ?? 0,
			currency: currency.code,
			contribution_schedule: useSchedule ? scheduleSteps.map((s) => ({ ...s })) : null,
			scenario_overrides: scenarioOverrides,
		})
	}

	const addStep = () => {
		const lastStep = scheduleSteps[scheduleSteps.length - 1]
		const fromYear = lastStep ? lastStep.to_year + 1 : 1
		setScheduleSteps((prev) => {
			const next = [...prev, { from_year: fromYear, to_year: fromYear + 4, monthly_amount: 0 }]
			updateSchedule(next)
			return next
		})
	}

	const addLumpSum = () => {
		const lastStep = scheduleSteps[scheduleSteps.length - 1]
		const year = lastStep ? lastStep.to_year + 1 : 1
		setScheduleSteps((prev) => {
			const next = [...prev, { from_year: year, to_year: year, monthly_amount: 0 }]
			updateSchedule(next)
			return next
		})
	}

	const removeStep = (index: number) => {
		setScheduleSteps((prev) => {
			const next = prev.filter((_, i) => i !== index)
			updateSchedule(next)
			return next
		})
	}

	const updateStep = (index: number, patch: Partial<ContributionStep>) => {
		setScheduleSteps((prev) => {
			const next = prev.map((s, i) => (i === index ? { ...s, ...patch } : s))
			updateSchedule(next)
			return next
		})
	}

	return (
		<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-200/50 dark:border-sky-800/30">
			<CardHeader className="cursor-pointer select-none" onClick={() => setExpanded(!expanded)}>
				<div className="flex items-center justify-between">
					<CardTitle className="text-base flex items-center gap-2">
						<Settings className="h-4 w-4" />
						FIRE Settings
					</CardTitle>
					{expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
				</div>
			</CardHeader>
			{expanded && (
				<CardContent>
					<form onSubmit={handleSubmit} className="space-y-4">
						<div className="space-y-1.5">
							<Label>Capital Start ({currency.symbol})</Label>
							<p className="text-lg font-semibold">{Math.round(portfolioValue ?? 0).toLocaleString()}</p>
							<p className="text-xs text-muted-foreground">
								Current portfolio value including cash
							</p>
						</div>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<div className="space-y-1.5">
								<Label>Monthly Expense Target ({currency.symbol})</Label>
								<Input
									type="text"
									inputMode="numeric"
									value={rawInputs.monthly_expense_target}
									onChange={(e) => {
										const raw = e.target.value
										setRawInputs((r) => ({ ...r, monthly_expense_target: raw }))
										updateForm({ monthly_expense_target: parseFloat(raw) || 0 })
									}}
								/>
								<p className="text-xs text-muted-foreground">
									Monthly expenses to cover with dividends
								</p>
							</div>
							<div className="space-y-1.5">
								<Label>Monthly Contribution ({currency.symbol})</Label>
								<Input
									type="text"
									inputMode="numeric"
									value={rawInputs.monthly_contribution}
									onChange={(e) => {
										const raw = e.target.value
										setRawInputs((r) => ({ ...r, monthly_contribution: raw }))
										updateForm({ monthly_contribution: parseFloat(raw) || 0 })
									}}
									disabled={useSchedule}
								/>
								<p className="text-xs text-muted-foreground">New money added monthly</p>
							</div>
						</div>

						{/* Contribution schedule toggle */}
						<div className="space-y-3">
							<div className="flex items-center gap-3">
								<Switch checked={useSchedule} onCheckedChange={handleToggleSchedule} />
								<Label className="flex items-center gap-1">
									Use contribution schedule
									<InfoTip content="Override the flat monthly contribution with different amounts for specific year ranges in your projection." />
								</Label>
							</div>

							{useSchedule && (
								<div className="space-y-2 pl-1">
									<p className="text-xs text-muted-foreground">
										Define contribution amounts for different year ranges. Years are relative (1 =
										next year).
									</p>
									{scheduleSteps.map((step, i) => (
										<div key={i} className="flex flex-wrap items-center gap-2">
											<Input
												type="text"
												inputMode="numeric"
												placeholder="From"
												value={step.from_year}
												onChange={(e) =>
													updateStep(i, { from_year: parseInt(e.target.value) || 0 })
												}
												className="w-20 text-xs"
											/>
											<span className="text-xs text-muted-foreground">to</span>
											<Input
												type="text"
												inputMode="numeric"
												placeholder="To"
												value={step.to_year}
												onChange={(e) =>
													updateStep(i, { to_year: parseInt(e.target.value) || 0 })
												}
												className="w-20 text-xs"
											/>
											<Input
												type="text"
												inputMode="numeric"
												placeholder={`${currency.symbol}/mo`}
												value={step.monthly_amount || ""}
												onChange={(e) =>
													updateStep(i, { monthly_amount: parseFloat(e.target.value) || 0 })
												}
												className="flex-1 text-xs"
											/>
											<button
												type="button"
												onClick={() => removeStep(i)}
												className="text-muted-foreground hover:text-destructive transition-colors p-1"
											>
												<Trash2 className="h-3.5 w-3.5" />
											</button>
										</div>
									))}
									<div className="flex gap-2">
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={addStep}
											className="gap-1 text-xs"
										>
											<Plus className="h-3 w-3" /> Add Step
										</Button>
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={addLumpSum}
											className="gap-1 text-xs"
										>
											<Plus className="h-3 w-3" /> Add Lump Sum
										</Button>
									</div>
								</div>
							)}
						</div>

						<div className="flex items-center gap-3">
							<Switch
								checked={form.drip_enabled}
								onCheckedChange={(checked) => updateForm({ drip_enabled: checked })}
							/>
							<Label className="flex items-center gap-1">
								DRIP (Dividend Reinvestment)
								<InfoTip content="When enabled, projected dividends are reinvested to buy more shares instead of being paid out as cash." />
							</Label>
						</div>

						<div>
							<button
								type="button"
								className="text-sm text-sky-600 dark:text-sky-400 flex items-center gap-1"
								onClick={() => setAdvancedExpanded(!advancedExpanded)}
							>
								{advancedExpanded ? (
									<ChevronUp className="h-3 w-3" />
								) : (
									<ChevronDown className="h-3 w-3" />
								)}
								Advanced settings
							</button>
							{advancedExpanded && (
								<div className="mt-4 space-y-4">
									{/* Scenario parameter table — mobile card layout */}
									<div className="md:hidden space-y-3">
										{/* Div Growth row */}
										<div className="rounded-lg border border-border/40 bg-muted/10 p-3 space-y-2">
											<p className="text-xs text-muted-foreground flex items-center gap-1">
												Div Growth (%/yr)
												<InfoTip
													content="Weighted average of each position's actual 5-year dividend CAGR. Not editable — derived from your holdings."
													side="right"
												/>
											</p>
											<div className="grid grid-cols-3 gap-2">
												<div className="space-y-1">
													<p
														className="text-xs font-medium text-center"
														style={{ color: "#f59e0b" }}
													>
														Conservative
													</p>
													<Input
														type="text"
														inputMode="decimal"
														value={scenarioRaw.conserv_div_growth}
														onChange={(e) => {
															const raw = e.target.value
															setScenarioRaw((r) => ({ ...r, conserv_div_growth: raw }))
															updateScenario({
																conservative: {
																	...scenarioOverrides.conservative,
																	expected_dividend_growth: parseFloat(raw) || 0,
																},
															})
														}}
														className="h-7 text-xs text-center"
													/>
												</div>
												<div className="space-y-1">
													<p
														className="text-xs font-medium text-center"
														style={{ color: "#0ea5e9" }}
													>
														Base
													</p>
													<div className="flex items-center gap-1">
														<Input
															type="text"
															value={baseDivGrowth != null ? baseDivGrowth.toFixed(1) : "—"}
															readOnly
															tabIndex={-1}
															className="h-7 text-xs text-center bg-muted cursor-default"
														/>
														<InfoTip
															content="Uses each position's actual 5-year dividend CAGR for the first 5 years, then settles to the income-weighted average for the rest of the projection."
															side="bottom"
														/>
													</div>
												</div>
												<div className="space-y-1">
													<p
														className="text-xs font-medium text-center"
														style={{ color: "#22c55e" }}
													>
														Optimistic
													</p>
													<Input
														type="text"
														inputMode="decimal"
														value={scenarioRaw.optim_div_growth}
														onChange={(e) => {
															const raw = e.target.value
															setScenarioRaw((r) => ({ ...r, optim_div_growth: raw }))
															updateScenario({
																optimistic: {
																	...scenarioOverrides.optimistic,
																	expected_dividend_growth: parseFloat(raw) || 0,
																},
															})
														}}
														className="h-7 text-xs text-center"
													/>
												</div>
											</div>
										</div>
										{/* Capital Appreciation row */}
										<div className="rounded-lg border border-border/40 bg-muted/10 p-3 space-y-2">
											<p className="text-xs text-muted-foreground flex items-center gap-1">
												Capital Appr. (%/yr)
												<InfoTip
													content="Annual rate at which stock prices are assumed to increase, affecting portfolio value growth."
													side="right"
												/>
											</p>
											<div className="grid grid-cols-3 gap-2">
												<div className="space-y-1">
													<p
														className="text-xs font-medium text-center"
														style={{ color: "#f59e0b" }}
													>
														Conservative
													</p>
													<Input
														type="text"
														inputMode="decimal"
														value={scenarioRaw.conserv_price_appr}
														onChange={(e) => {
															const raw = e.target.value
															setScenarioRaw((r) => ({ ...r, conserv_price_appr: raw }))
															updateScenario({
																conservative: {
																	...scenarioOverrides.conservative,
																	price_appreciation: parseFloat(raw) || 0,
																},
															})
														}}
														className="h-7 text-xs text-center"
													/>
												</div>
												<div className="space-y-1">
													<p
														className="text-xs font-medium text-center"
														style={{ color: "#0ea5e9" }}
													>
														Base
													</p>
													<div className="flex items-center gap-1">
														<Input
															type="text"
															inputMode="decimal"
															value={rawInputs.price_appreciation}
															onChange={(e) => {
																const raw = e.target.value
																setRawInputs((r) => ({ ...r, price_appreciation: raw }))
																updateForm({ price_appreciation: parseFloat(raw) || 0 })
															}}
															className="h-7 text-xs text-center"
														/>
														<InfoTip
															content="Your assumed annual rate of stock price growth. The default of 6% reflects long-term broad equity market averages (nominal, before inflation). Adjust based on your asset mix and market outlook."
															side="bottom"
														/>
													</div>
												</div>
												<div className="space-y-1">
													<p
														className="text-xs font-medium text-center"
														style={{ color: "#22c55e" }}
													>
														Optimistic
													</p>
													<Input
														type="text"
														inputMode="decimal"
														value={scenarioRaw.optim_price_appr}
														onChange={(e) => {
															const raw = e.target.value
															setScenarioRaw((r) => ({ ...r, optim_price_appr: raw }))
															updateScenario({
																optimistic: {
																	...scenarioOverrides.optimistic,
																	price_appreciation: parseFloat(raw) || 0,
																},
															})
														}}
														className="h-7 text-xs text-center"
													/>
												</div>
											</div>
										</div>
									</div>

									{/* Scenario parameter table — desktop layout */}
									<div className="hidden md:block overflow-x-auto">
										<table className="w-full text-sm">
											<thead>
												<tr>
													<th className="text-left text-xs text-muted-foreground font-normal pb-2 pr-3 w-40"></th>
													<th
														className="text-center text-xs font-medium pb-2 px-2"
														style={{ color: "#f59e0b" }}
													>
														Conservative
													</th>
													<th
														className="text-center text-xs font-medium pb-2 px-2"
														style={{ color: "#0ea5e9" }}
													>
														Base
													</th>
													<th
														className="text-center text-xs font-medium pb-2 px-2"
														style={{ color: "#22c55e" }}
													>
														Optimistic
													</th>
												</tr>
											</thead>
											<tbody className="space-y-2">
												<tr>
													<td className="text-xs text-muted-foreground py-1.5 pr-3">
														<span className="flex items-center gap-1">
															Div Growth (%/yr)
															<InfoTip
																content="Weighted average of each position's actual 5-year dividend CAGR. Not editable — derived from your holdings."
																side="right"
															/>
														</span>
													</td>
													<td className="py-1.5 px-2">
														<Input
															type="text"
															inputMode="decimal"
															value={scenarioRaw.conserv_div_growth}
															onChange={(e) => {
																const raw = e.target.value
																setScenarioRaw((r) => ({
																	...r,
																	conserv_div_growth: raw,
																}))
																updateScenario({
																	conservative: {
																		...scenarioOverrides.conservative,
																		expected_dividend_growth: parseFloat(raw) || 0,
																	},
																})
															}}
															className="h-7 text-xs text-center"
														/>
													</td>
													<td className="py-1.5 px-2">
														<div className="flex items-center gap-1">
															<Input
																type="text"
																value={
																	baseDivGrowth != null ? baseDivGrowth.toFixed(1) : "—"
																}
																readOnly
																tabIndex={-1}
																className="h-7 text-xs text-center bg-muted cursor-default"
															/>
															<InfoTip
																content="Uses each position's actual 5-year dividend CAGR for the first 5 years, then settles to the income-weighted average for the rest of the projection."
																side="bottom"
															/>
														</div>
													</td>
													<td className="py-1.5 px-2">
														<Input
															type="text"
															inputMode="decimal"
															value={scenarioRaw.optim_div_growth}
															onChange={(e) => {
																const raw = e.target.value
																setScenarioRaw((r) => ({ ...r, optim_div_growth: raw }))
																updateScenario({
																	optimistic: {
																		...scenarioOverrides.optimistic,
																		expected_dividend_growth: parseFloat(raw) || 0,
																	},
																})
															}}
															className="h-7 text-xs text-center"
														/>
													</td>
												</tr>
												<tr>
													<td className="text-xs text-muted-foreground py-1.5 pr-3">
														<span className="flex items-center gap-1">
															Capital Appr. (%/yr)
															<InfoTip
																content="Annual rate at which stock prices are assumed to increase, affecting portfolio value growth."
																side="right"
															/>
														</span>
													</td>
													<td className="py-1.5 px-2">
														<Input
															type="text"
															inputMode="decimal"
															value={scenarioRaw.conserv_price_appr}
															onChange={(e) => {
																const raw = e.target.value
																setScenarioRaw((r) => ({
																	...r,
																	conserv_price_appr: raw,
																}))
																updateScenario({
																	conservative: {
																		...scenarioOverrides.conservative,
																		price_appreciation: parseFloat(raw) || 0,
																	},
																})
															}}
															className="h-7 text-xs text-center"
														/>
													</td>
													<td className="py-1.5 px-2">
														<div className="flex items-center gap-1">
															<Input
																type="text"
																inputMode="decimal"
																value={rawInputs.price_appreciation}
																onChange={(e) => {
																	const raw = e.target.value
																	setRawInputs((r) => ({ ...r, price_appreciation: raw }))
																	updateForm({ price_appreciation: parseFloat(raw) || 0 })
																}}
																className="h-7 text-xs text-center"
															/>
															<InfoTip
																content="Your assumed annual rate of stock price growth. The default of 6% reflects long-term broad equity market averages (nominal, before inflation). Adjust based on your asset mix and market outlook."
																side="bottom"
															/>
														</div>
													</td>
													<td className="py-1.5 px-2">
														<Input
															type="text"
															inputMode="decimal"
															value={scenarioRaw.optim_price_appr}
															onChange={(e) => {
																const raw = e.target.value
																setScenarioRaw((r) => ({ ...r, optim_price_appr: raw }))
																updateScenario({
																	optimistic: {
																		...scenarioOverrides.optimistic,
																		price_appreciation: parseFloat(raw) || 0,
																	},
																})
															}}
															className="h-7 text-xs text-center"
														/>
													</td>
												</tr>
											</tbody>
										</table>
									</div>

									{/* Dividend Growth Cap — shared across all scenarios */}
									<div className="space-y-1.5">
										<Label className="flex items-center gap-1">
											Dividend Growth Cap (%)
											<InfoTip content="Maximum annual dividend growth rate for any single position. Prevents outliers from skewing the projection." />
											<span className="text-xs text-muted-foreground font-normal">
												— applies to all scenarios
											</span>
										</Label>
										<Input
											type="text"
											inputMode="numeric"
											value={rawInputs.dividend_growth_cap}
											onChange={(e) => {
												const raw = e.target.value
												setRawInputs((r) => ({ ...r, dividend_growth_cap: raw }))
												updateForm({ dividend_growth_cap: parseFloat(raw) || 0 })
											}}
											className="w-32"
										/>
									</div>
								</div>
							)}
						</div>

						<Button type="submit" disabled={saving} className="bg-sky-500 hover:bg-sky-600 text-white">
							{saving ? "Saving..." : "Save Settings"}
						</Button>
					</form>
				</CardContent>
			)}
		</Card>
	)
}
